// Contact book model (docs/ideas.md "Contact Book"). Contacts are real,
// persisted entities a detected speaker can be bound to, so a recurring person
// keeps the same name, colour and avatar across sessions. Browser-only, stored
// in IndexedDB alongside keys + sessions — nothing leaves this machine.

/** A saved person. `avatar` is a data URL (uploaded image) or null → initials. */
export interface Contact {
  id: string
  name: string
  /** Semantic Tailwind text-color utility, e.g. 'text-accent'. */
  color: string
  /** Data URL of an uploaded image, or null to fall back to initials. */
  avatar: string | null
  createdAt: number
}

// The prefab palette offered in the picker — the same six semantic colours the
// session store assigns to speakers, so a contact's colour reads identically in
// the transcript.
export const CONTACT_COLORS = [
  'text-accent',
  'text-success',
  'text-warn',
  'text-danger',
  'text-text',
  'text-muted-foreground'
] as const

// A speaker as the display layer sees it — kept minimal so this module doesn't
// depend on the transcription types (avoids a cycle).
export interface SpeakerLike {
  providerLabel: string
  displayName: string | null
  color: string
  contactId?: string | null
}

export interface Identity {
  name: string
  color: string
  avatar: string | null
}

/** Resolve how a speaker should render: a linked contact's name/colour/avatar
 *  win, else the speaker's own name (or provider label) and colour. */
export function resolveIdentity(sp: SpeakerLike, contact?: Contact): Identity {
  return {
    name: contact?.name ?? sp.displayName ?? sp.providerLabel,
    color: contact?.color ?? sp.color,
    avatar: contact?.avatar ?? null
  }
}

/** Up to two initials from a name, e.g. "Tobias Strebitzer" → "TS". */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase() || '?'
}
